import {
  cardWatcherResolutionLabel,
  type CardCloser,
  type CardWatcherResolution,
} from '@littlebird/action-cards';

import type { CardRow, CardState } from '@littlebird/action-cards-view';

export type HistoryOutcome = 'done' | 'dismissed' | 'withdrawn';

export type HistoryRowView = {
  title: string;
  outcome: HistoryOutcome;
  meta: string;
  openUrl: string | null;
  // Restore shows on a dismissed card unless the server says its action can no longer run, and on
  // a card its watcher marked done when the server allows it. The document's slot says why when it
  // may not (`/restore/blocked_reason`)
  canRestore: boolean;
  // What the control that brings the card back says: a done card was marked done, by the user or
  // its watcher, so it is marked not done
  restoreLabel: string;
};

const DISMISSED = 'Dismissed';
const WITHDRAWN = 'Withdrawn';
const DISMISSED_BY_LITTLEBIRD = 'Dismissed by Littlebird';
const DONE_BY_LITTLEBIRD = 'Marked done by Littlebird';
const EXPIRED = 'Expired';
const RESTORE = 'Restore';
const MARK_NOT_DONE = 'Mark not done';

export const historyOutcomeOf = (
  status: CardRow['status']
): HistoryOutcome | null => {
  if (status === 'completed' || status === 'completed_externally')
    return 'done';
  if (status === 'dismissed') return 'dismissed';
  if (status === 'revoked') return 'withdrawn';
  return null;
};

/** What a card Littlebird took back reads: "Withdrawn", with the reason when the server gave one */
export const withdrawnTextOf = (state: CardState): string =>
  state.revoked_reason ? `${WITHDRAWN} · ${state.revoked_reason}` : WITHDRAWN;

const metaOf = (outcome: HistoryOutcome, state: CardState): string => {
  switch (outcome) {
    case 'done':
      return (
        (state.status === 'completed' ? state.run?.done_text : null) ??
        state.outcome?.label ??
        'Done'
      );
    case 'dismissed':
      return state.dismissed_reason
        ? `${DISMISSED} · ${state.dismissed_reason}`
        : DISMISSED;
    case 'withdrawn':
      return withdrawnTextOf(state);
  }
};

const withDetail = (label: string, detail: string | null | undefined) =>
  detail ? `${label} · ${detail}` : label;

// A card Littlebird or its expiry closed says so. A run keeps its done text ("Sent to Dana"); a
// tick's done text only repeats "Marked done"
const closedMetaOf = (
  outcome: HistoryOutcome,
  state: CardState,
  closer: CardCloser
): string | null => {
  if (outcome === 'dismissed')
    return closer === 'expiry'
      ? EXPIRED
      : withDetail(DISMISSED_BY_LITTLEBIRD, state.dismissed_reason);
  if (outcome === 'done' && closer === 'agent') {
    const ran =
      state.status === 'completed' && state.run?.action !== 'acknowledge';
    return withDetail(DONE_BY_LITTLEBIRD, ran ? state.run?.done_text : null);
  }
  return null;
};

// History reads only served facts, and each may be missing on an older row. A card its watcher
// resolved says so, with the watcher's reason; one an agent or its expiry closed says that
export function historyRowView(
  state: CardState,
  watcherResolution: CardWatcherResolution | null = null,
  closer: CardCloser | null = null
): HistoryRowView | null {
  const outcome = historyOutcomeOf(state.status);
  if (!outcome) return null;
  return {
    title: state.title,
    outcome,
    meta: watcherResolution
      ? cardWatcherResolutionLabel(watcherResolution)
      : ((closer && closedMetaOf(outcome, state, closer)) ??
        metaOf(outcome, state)),
    openUrl: outcome === 'done' ? (state.open_url ?? null) : null,
    canRestore:
      (outcome === 'dismissed' && state.restore?.allowed !== false) ||
      (outcome === 'done' && state.restore?.allowed === true),
    restoreLabel: outcome === 'done' ? MARK_NOT_DONE : RESTORE,
  };
}
